import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import encryptedStorage from '../utils/encryptedStorage';
import { setAccessToken, getAccessToken, clearAccessToken } from '../utils/tokenManager';
import axiosInstance, { getSession } from '../utils/axiosConfig';
import { FAST_API_BASE_URL } from '../constants';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [currentDomain, setCurrentDomain] = useState(null);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [showRebrandingPage, setShowRebrandingPage] = useState(false);
  const [showMergeDomainsPage, setShowMergeDomainsPage] = useState(false);
  const navigate= useNavigate()
  const checkResellerStatus = useCallback(async (userId) => {
    if (!userId) return;
    
    try {
      const token = getAccessToken();
      const response = await axiosInstance.get(
        `${FAST_API_BASE_URL}/api/reseller-status/${userId}`,
        {
          headers: {
            "x-api-key": process.env.REACT_APP_API_KEY,
             'Authorization': `Bearer ${token}`
          },
          withCredentials: true
        }
      );

      if (response.data.resultType === "Success" && response.data.result) {
        const { 
          is_whitelabelling_reseller_only, 
          is_whitelabel_user,
          allow_merge_domains 
        } = response.data.result;
        
        setShowRebrandingPage(is_whitelabel_user === true);
        setShowMergeDomainsPage(allow_merge_domains === true);
      }
    } catch (error) {
      console.error("Error checking reseller status:", error);
      setShowRebrandingPage(false);
      setShowMergeDomainsPage(false);
    }
  }, []);

  const fetchUserSites = useCallback(async () => {
    try {
      const token = getAccessToken();
      if (!token) return [];
      
      const response = await axiosInstance.get(`${FAST_API_BASE_URL}/api/user/domains`, {
         headers: {
            "x-api-key": process.env.REACT_APP_API_KEY,
             'Authorization': `Bearer ${token}`
          },

        withCredentials: true
      });
      
      if (response.data?.result && Array.isArray(response.data.result.domains)) {
        return response.data.result.domains;
      }
      return [];
    } catch (error) {
      console.error("Failed to fetch user sites:", error);
      return [];
    }
  }, []);

  const initializeAuth = useCallback(async () => {
    if (!encryptedStorage.isAvailable()) {
      console.error('Session storage is not available');
      setLoading(false);
      setIsInitialized(true);
      return;
    }

    try {
      const storedUser = encryptedStorage.getItem('auth_user');
      const storedDomain = encryptedStorage.getItem('current_domain');
      const storedSites = encryptedStorage.getItem('user_sites');

      if (storedUser) {
        setUser(storedUser);
        setSites(storedSites || []);
        
        if (storedDomain) {
          setCurrentDomain(storedDomain);
        } else if (storedSites && storedSites.length > 0) {
          setCurrentDomain(storedSites[0]);
        }
        
        await checkResellerStatus(storedUser.id);
      }
    } catch (error) {
      console.error('Error initializing auth:', error);
    } finally {
      setLoading(false);
      setIsInitialized(true);
    }
  }, [checkResellerStatus]);
useEffect(() => {
  const init = async () => {
    await getSession();
    setIsInitialized(true);
    setLoading(false);
  };

  init();
}, []);
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  const login = useCallback(async (userData, domains = []) => {
    try {
      if (userData.access_token) {
        setAccessToken(userData.access_token);
      }
      
      encryptedStorage.setItem('auth_user', {
        id: userData.id,
        email: userData.email,
        name: userData.name,
        profile_pic: userData.profile_pic,
        is_super_admin: userData.is_super_admin,
        is_whitelabel_user: userData.is_whitelabel_user,
        is_whitelabelling_reseller_only: userData.is_whitelabelling_reseller_only || false,
        user_token: userData.user_token,
        role: userData.role
      });
      
      encryptedStorage.setItem('user_sites', domains);
      
      let initialDomain = null;
      if (domains && domains.length > 0) {
        initialDomain = domains[0];
        encryptedStorage.setItem('current_domain', initialDomain);
        
        if (initialDomain.role) {
          localStorage.setItem('role', initialDomain.role);
        }
        
        if (initialDomain.source) {
          localStorage.setItem('source', initialDomain.source);
        }
      }
      
      if (userData.role) {
        localStorage.setItem('role', userData.role);
      }
      
      if (userData.is_super_admin) {
        localStorage.setItem('is_super_admin', 'true');
      } else {
        localStorage.setItem('is_super_admin', 'false');
      }
      
      setUser({
        id: userData.id,
        email: userData.email,
        name: userData.name,
        profile_pic: userData.profile_pic,
        is_super_admin: userData.is_super_admin,
        is_whitelabel_user: userData.is_whitelabel_user,
        is_whitelabelling_reseller_only: userData.is_whitelabelling_reseller_only || false,
        secret_token: userData.secret_token,
        role: userData.role
      });
      
      setSites(domains);
      setCurrentDomain(initialDomain);
      
      await checkResellerStatus(userData.id);
      
      return true;
    } catch (error) {
      console.error('Login failed:', error);
      return false;
    }
  }, [checkResellerStatus]);

  const logout = useCallback(async () => {
    console.log('Logging out user...');
    
    try {
      const accessToken = getAccessToken();
      if (accessToken) {
        await axiosInstance.post(`${FAST_API_BASE_URL}/api/auth/logout`, {}, {
          headers: {
            'Authorization': `Bearer ${accessToken}`
          },
          withCredentials: true
        });
        //console.log('✅ Backend session revoked');
      }
    } catch (error) {
      console.error('Logout API error:', error);
    }
    
    const authKeys = [
      'auth_user', 'user_sites', 'current_domain',
      'domain_token', 'secret', 'url_domain_id', 'role'
    ];
    
    authKeys.forEach(key => {
      try {
       // console.log(`Removing ${key} from encrypted storage`);
        encryptedStorage.removeItem(key);
      } catch (e) {
        console.error(`Failed to remove ${key}:`, e);
      }
    });
    
    const sessionKeys = [
      'sessionConfig', 'inspection_mode', 'inspected_email',
      'original_user_id', 'role'
    ];
    
    sessionKeys.forEach(key => {
      try {
        sessionStorage.removeItem(key);
      } catch (e) {
        console.error(`Failed to remove session ${key}:`, e);
      }
    });
    
    const localAuthKeys = [
      'sessionConfig', 'rememberMe', 'role', 'source',
      'is_super_admin', 'showStayLoggedInPrompt', 'lastActivityTime',
      'domain_id', 'secret'
    ];
    
    localAuthKeys.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (e) {
        console.error(`Failed to remove localStorage ${key}:`, e);
      }
    });
    
    clearAccessToken();
    
    setUser(null);
    setCurrentDomain(null);
    setSites([]);
    setShowRebrandingPage(false);
    setShowMergeDomainsPage(false);
    navigate("/login")
    
    //console.log('✅ Local session cleared');
  }, []);

  const switchDomain = useCallback(async (domainId) => {
    if (!sites || sites.length === 0) return false;
    
    const domain = sites.find(d => d.domain_token === domainId);
    if (domain) {
      encryptedStorage.setItem('current_domain', domain);
      setCurrentDomain(domain);
      
      if (domain.role) {
        localStorage.setItem('role', domain.role);
      }
      
      if (domain.source) {
        localStorage.setItem('source', domain.source);
      }
      
      // try {
      //   const token = getAccessToken();
      //   if (token) {
      //     await axiosInstance.post(`${FAST_API_BASE_URL}/api/user/switch-domain`, {
      //       domain_id: domainId
      //     }, {
      //       withCredentials: true
      //     });
      //   }
      // } catch (error) {
      //   console.error('Failed to notify domain switch:', error);
      // }
      
      return true;
    }
    return false;
  }, [sites]);

  const updateSites = useCallback(async (newSites) => {
    if (!newSites || !Array.isArray(newSites)) return;
    
    encryptedStorage.setItem('user_sites', newSites);
    setSites(newSites);
    
    if (currentDomain) {
      const domainExists = newSites.find(s => s.domain_token === currentDomain.domain_token);
      
      if (!domainExists) {
        const newDomain = newSites[0] || null;
        if (newDomain) {
          encryptedStorage.setItem('current_domain', newDomain);
          setCurrentDomain(newDomain);
          
          if (newDomain.role) {
            localStorage.setItem('role', newDomain.role);
          }
          
          if (newDomain.source) {
            localStorage.setItem('source', newDomain.source);
          }
        } else {
          encryptedStorage.removeItem('current_domain');
          setCurrentDomain(null);
        }
      }
    }
  }, [currentDomain]);

  const getDomains = useCallback(() => {
    return sites || [];
  }, [sites]);

const [authVersion, setAuthVersion] = useState(0);

useEffect(() => {
  const handleAuthChange = () => {
    setAuthVersion(prev => prev + 1);
  };

  window.addEventListener("authChanged", handleAuthChange);

  return () => {
    window.removeEventListener("authChanged", handleAuthChange);
  };
}, []);

const isAuthenticated = useCallback(() => {
  const hasUserInStorage = !!encryptedStorage.getItem('auth_user');
  const hasAccessToken = !!getAccessToken();

  return hasUserInStorage && hasAccessToken;
}, [authVersion]);

  const getAuthHeaders = useCallback(() => {
    const currentUser = user || encryptedStorage.getItem('auth_user');
    const token = getAccessToken();
    
    const headers = {};
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    if (currentUser?.email) {
      headers['EMAIL'] = currentUser.email;
    }
    
    const domain = currentDomain || encryptedStorage.getItem('current_domain');
    if (domain?.domain_token) {
      headers['DOMAIN-TOKEN'] = domain.domain_token;
    } else if (domain?.domain_id) {
      headers['DOMAIN-TOKEN'] = domain.domain_id;
    }
    
    const secretToken = encryptedStorage.getItem('secret');
    if (secretToken) {
      headers['SECRET-TOKEN'] = secretToken;
    }
    
    return headers;
  }, [user, currentDomain]);

  const getCurrentRole = useCallback(() => {
    return localStorage.getItem('role') || user?.role || currentDomain?.role || null;
  }, [user, currentDomain]);

  const refreshResellerStatus = useCallback(async () => {
    if (user?.id) {
      await checkResellerStatus(user.id);
    }
  }, [user, checkResellerStatus]);

  const refreshUserData = useCallback(async () => {
    try {
      const token = getAccessToken();
      if (!token) return false;
      
      const newSites = await fetchUserSites();
      if (newSites.length > 0) {
        updateSites(newSites);
      }
      
      if (user?.id) {
        await checkResellerStatus(user.id);
      }
      
      return true;
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      return false;
    }
  }, [user, fetchUserSites, updateSites, checkResellerStatus]);

  const value = {
    user,
    currentDomain,
    sites,
    loading,
    isInitialized,
    showRebrandingPage,
    showMergeDomainsPage,
    login,
    logout,
    switchDomain,
    updateSites,
    setCurrentDomain,
    getDomains,
    isAuthenticated,
    getAuthHeaders,
    getCurrentRole,
    refreshResellerStatus,
    refreshUserData,
    fetchUserSites
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;